import React, { useState, useEffect, useCallback } from 'react';

const SEV_LABEL = {
  grave: 'Grave · 3–8',
  moderado: 'Moderado · 9–12',
  leve: 'Leve · 13–15',
};

const severityOf = (score) => {
  if (typeof score !== 'number') return null;
  if (score >= 13) return 'leve';
  if (score >= 9) return 'moderado';
  return 'grave';
};

const ScoreReadout = ({ selections, onSeverityChange }) => {
  const [copied, setCopied] = useState(false);

  const parts = [
    ['O', selections.eye],
    ['V', selections.verbal],
    ['M', selections.motor],
  ];

  const filled = parts.filter(([, value]) => value !== null && value !== undefined);
  const isComplete = filled.length === 3;
  const hasNT = parts.some(([, value]) => value === 'NT');
  const hasPupils = selections.pupils !== null && selections.pupils !== undefined;

  const total = isComplete && !hasNT
    ? selections.eye + selections.verbal + selections.motor
    : null;

  const severity = severityOf(total);
  const gcsp = hasPupils && typeof total === 'number'
    ? Math.max(1, total - selections.pupils)
    : null;

  let state = 'empty';
  if (filled.length > 0 && !isComplete) state = 'partial';
  else if (hasNT) state = 'nt';
  else if (severity) state = severity;

  const formatSlot = (key, value) => {
    if (value === null || value === undefined) return `${key}—`;
    if (value === 'NT') return `${key}(NT)`;
    return `${key}${value}`;
  };

  const componentNotation = parts.map(([key, value]) => formatSlot(key, value)).join(' ');
  const ntComponents = parts.filter(([, value]) => value === 'NT').map(([key]) => key).join(', ');

  let recordPreview = componentNotation;
  if (state === 'empty' || state === 'partial') {
    recordPreview += '\nSelecione os três componentes';
  } else if (state === 'nt') {
    recordPreview += `\nTotal não somado\n${ntComponents} marcado como não testável`;
  } else {
    recordPreview += `\nECG ${total} · ${SEV_LABEL[severity]}`;
    if (gcsp !== null) recordPreview += `\nERP ${selections.pupils} · ECG-P ${gcsp}`;
  }

  useEffect(() => {
    onSeverityChange(severity);
  }, [severity, onSeverityChange]);

  const canCopy = state !== 'empty' && state !== 'partial';

  const handleCopy = useCallback(() => {
    if (!canCopy) return;

    const confirm = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(recordPreview).then(confirm, () => fallbackCopy(recordPreview, confirm));
    } else {
      fallbackCopy(recordPreview, confirm);
    }
  }, [canCopy, recordPreview]);

  const fallbackCopy = (text, done) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      done();
    } catch (err) {
      /* sem clipboard disponível: silencioso, nunca alert() */
    }
    document.body.removeChild(ta);
  };

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c') {
        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) return;
        if (!canCopy) return;
        event.preventDefault();
        handleCopy();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canCopy, handleCopy]);

  return (
    <aside className="ro" role="status" aria-live="polite" data-state={state}>
      <span className="ro__tab mono">REGISTRO</span>

      <pre className="ro__preview mono">{recordPreview}</pre>

      <div className="ro__acts">
        <button className="btn btn--pri" onClick={handleCopy} disabled={!canCopy}>
          Copiar para prontuário
        </button>
        <button className="btn" onClick={() => window.print()}>
          Imprimir
        </button>
      </div>

      <p className="ro__ok mono" hidden={!copied}>
        copiado
      </p>
    </aside>
  );
};

export default ScoreReadout;
